import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, CalendarDays, Clock, MapPin, Plus, X } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import PlacesAutocomplete from "./PlacesAutocomplete";
import { useLang } from "../i18n/LanguageContext";
import { useLocalizedPath } from "../i18n/langPath";
import { ZURICH_AIRPORT, TIME_OPTIONS, defaultDateTime, zurichNow } from "../data/defaults";
import VoiceButton from "./VoiceButton";
import { voiceStr } from "../data/voiceStrings";
import { voiceBooking, placesAutocomplete } from "../lib/api";

const times = TIME_OPTIONS;
const DEFAULTS = defaultDateTime();
const pad = (n) => String(n).padStart(2, "0");

const container = { hidden: {}, show: { transition: { staggerChildren: 0.05, delayChildren: 0.03 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } } };

const boxCls = "w-full h-16 rounded-2xl bg-white border border-neutral-200 px-4 flex items-center gap-3 text-[16px] font-medium text-neutral-800 hover:border-neutral-300 transition-colors";

export default function BookingWidget({ initialDropoff = null }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const lp = useLocalizedPath();
  const [pickup, setPickup] = useState(ZURICH_AIRPORT);
  const [dropoff, setDropoff] = useState(initialDropoff);
  const [date, setDate] = useState(DEFAULTS.date);
  const [dateOpen, setDateOpen] = useState(false);
  const [time, setTime] = useState(DEFAULTS.time);
  const [roundTrip, setRoundTrip] = useState(false);
  const [returnDate, setReturnDate] = useState(DEFAULTS.date);
  const [returnDateOpen, setReturnDateOpen] = useState(false);
  const [returnTime, setReturnTime] = useState(DEFAULTS.time);
  const [stops, setStops] = useState([]);

  const addStop = () => setStops((s) => (s.length < 3 ? [...s, null] : s));
  const setStop = (i, v) => setStops((s) => s.map((x, idx) => (idx === i ? v : x)));
  const removeStop = (i) => setStops((s) => s.filter((_, idx) => idx !== i));
  const swap = () => { setPickup(dropoff); setDropoff(pickup); };

  // Block picking a pickup time in the past when the date is today.
  const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const timeToDate = (d, tm) => { const [h, m] = String(tm || "0:0").split(":").map(Number); const x = new Date(d); x.setHours(h || 0, m || 0, 0, 0); return x; };
  const isPastSlot = (d, tm) => !!(d && tm && sameDay(d, zurichNow()) && timeToDate(d, tm) < zurichNow());

  const locale = { de: "de-CH", en: "en-GB", fr: "fr-CH", it: "it-CH" }[lang] || "de-CH";
  const fmtDate = (d) => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : t("b_date_ph"));
  // Local calendar date (YYYY-MM-DD) WITHOUT UTC shift. toISOString() would move
  // the date back a day for positive UTC offsets (Switzerland is UTC+1/+2).
  const fmtLocalDate = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : null);

  const submit = () => {
    if (!pickup || !dropoff) {
      toast({ title: t("b_addr_err"), description: t("b_addr_err_d") });
      return;
    }
    const cleanStops = stops.filter(Boolean).map((p) => ({ place_id: p.place_id, address: p.description }));
    navigate(lp("/booking"), { state: {
      mode: "transfer", pickup, dropoff, hours: 2,
      date: fmtLocalDate(date), time,
      round_trip: roundTrip,
      return_date: roundTrip ? fmtLocalDate(returnDate) : null,
      return_time: roundTrip ? returnTime : "",
      stops: cleanStops,
    } });
  };

  // Voice → Booking
  const vstr = voiceStr(lang);
  const [voiceHeard, setVoiceHeard] = useState("");
  const [voiceMsg, setVoiceMsg] = useState("");
  const resolvePlace = async (name) => {
    if (!name) return null;
    try {
      const preds = await placesAutocomplete(name, undefined, lang);
      if (preds?.[0]) return { description: preds[0].description, place_id: preds[0].place_id };
    } catch { /* ignore */ }
    return null;
  };
  const handleVoice = async (blob) => {
    setVoiceMsg(""); setVoiceHeard("");
    let data;
    try {
      data = await voiceBooking(blob, lang);
    } catch (e) {
      setVoiceMsg(e?.response?.data?.detail || vstr.not_understood);
      return;
    }
    const p = data.parsed || {};
    setVoiceHeard(data.transcript || "");
    const _pickup = p.pickup ? (await resolvePlace(p.pickup)) || ZURICH_AIRPORT : ZURICH_AIRPORT;
    const _dropoff = p.dropoff ? await resolvePlace(p.dropoff) : null;
    const _date = p.date ? new Date(p.date + "T00:00:00") : date;
    const _time = p.time && /^\d{1,2}:\d{2}$/.test(p.time) ? p.time : time;
    const rt = !!p.round_trip;
    setPickup(_pickup);
    if (_dropoff) setDropoff(_dropoff);
    if (!isNaN(_date)) setDate(_date);
    setTime(_time);
    setRoundTrip(rt);
    if (_pickup && _dropoff && _date && _time) {
      navigate(lp("/booking"), { state: {
        mode: "transfer", pickup: _pickup, dropoff: _dropoff, hours: 2,
        date: !isNaN(_date) ? fmtLocalDate(_date) : null, time: _time,
        round_trip: rt, return_date: rt && !isNaN(_date) ? fmtLocalDate(_date) : null, return_time: rt ? _time : "",
        stops: [], pax: p.passengers || 1, bags: p.luggage != null ? p.luggage : 1, vehicle: p.vehicle_class || "", voice: true,
      } });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-[460px] mx-auto rounded-[2rem] bg-white shadow-[0_32px_64px_-12px_rgba(0,0,0,0.22)] p-6 md:p-7"
      data-testid="booking-widget"
    >
      <motion.div variants={container} initial="hidden" animate="show" className="flex flex-col gap-3.5">
        {/* Voice → Booking (kept) */}
        <motion.div variants={item} data-testid="voice-widget-block">
          <VoiceButton onAudio={handleVoice} str={vstr} data-testid="voice-widget-button" />
          {voiceHeard ? (
            <p className="mt-2 text-xs text-neutral-500" data-testid="voice-widget-heard">
              <span className="font-semibold text-neutral-700">{vstr.heard}:</span> {voiceHeard}
            </p>
          ) : null}
          {voiceMsg && <p className="mt-1.5 text-xs text-red-600 font-medium" data-testid="voice-widget-error">{voiceMsg}</p>}
        </motion.div>

        {/* Pickup (with dark swap circle at its right) + Destination */}
        <motion.div variants={item} className="flex flex-col gap-3">
          <div className="relative">
            <PlacesAutocomplete outline icon={MapPin} iconClass="text-neutral-500" value={pickup} onChange={setPickup} placeholder={t("b_from_ph")} />
            <button type="button" onClick={swap} data-testid="booking-swap-btn"
              aria-label={t("b_swap")} title={t("b_swap")}
              className="absolute end-2 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-[#0b1f3d] text-white flex items-center justify-center shadow-md hover:bg-[#F5B400] hover:text-black active:scale-95 transition-all">
              <ArrowUpDown className="w-5 h-5" />
            </button>
          </div>
          <PlacesAutocomplete outline icon={MapPin} iconClass="text-neutral-500" value={dropoff} onChange={setDropoff} placeholder={t("b_to_ph")} />
        </motion.div>

        {/* Stops */}
        {stops.map((st, i) => (
          <motion.div variants={item} key={i} className="flex items-center gap-2" data-testid={`booking-stop-${i}`}>
            <div className="flex-1"><PlacesAutocomplete outline icon={MapPin} iconClass="text-neutral-500" value={st} onChange={(v) => setStop(i, v)} placeholder={`${t("b_stop")} ${i + 1}`} /></div>
            <button type="button" onClick={() => removeStop(i)} data-testid={`booking-remove-stop-${i}`}
              className="w-12 h-16 shrink-0 rounded-2xl bg-neutral-100 text-neutral-500 flex items-center justify-center hover:bg-neutral-200 transition-colors"><X className="w-4 h-4" /></button>
          </motion.div>
        ))}
        {stops.some(Boolean) && <p className="text-xs text-neutral-500 -mt-1" data-testid="booking-stop-note">{t("b_stop_note")}</p>}

        {/* Date + Time */}
        <motion.div variants={item} className="grid grid-cols-2 gap-3">
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button className={boxCls} data-testid="booking-date">
                <CalendarDays className="w-[18px] h-[18px] text-neutral-500 shrink-0" />
                <span className="truncate">{fmtDate(date)}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-neutral-100 rounded-2xl" align="start">
              <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setDateOpen(false); } }} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
            </PopoverContent>
          </Popover>

          <Select value={time} onValueChange={setTime}>
            <SelectTrigger className={boxCls} data-testid="booking-time">
              <span className="!flex items-center gap-3 flex-1 text-start"><Clock className="w-[18px] h-[18px] text-neutral-500 shrink-0" /><SelectValue placeholder={t("b_time_ph")} /></span>
            </SelectTrigger>
            <SelectContent className="bg-white border-neutral-100 rounded-2xl max-h-64">
              {times.map((tm) => (<SelectItem key={tm} value={tm} disabled={isPastSlot(date, tm)} className="focus:bg-neutral-50">{tm}</SelectItem>))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Extras: add stops + round trip */}
        <motion.div variants={item} className="flex items-center gap-6 px-1 pt-0.5">
          {stops.length < 3 && (
            <button type="button" onClick={addStop} data-testid="booking-add-stop"
              className="flex items-center gap-2 text-[16px] font-medium text-neutral-800 hover:text-neutral-500 transition-colors">
              <Plus className="w-5 h-5" strokeWidth={2.5} /> {t("bw_stops")}
            </button>
          )}
          <button type="button" onClick={() => setRoundTrip((v) => !v)} data-testid="booking-trip-toggle"
            className={`flex items-center gap-2 text-[16px] font-medium transition-colors ${roundTrip ? "text-neutral-900 font-semibold" : "text-neutral-800 hover:text-neutral-500"}`}>
            <ArrowUpDown className="w-5 h-5" strokeWidth={2.5} /> {t("bw_return")}
          </button>
        </motion.div>

        {/* Return date + time (round trip) */}
        {roundTrip && (
          <motion.div variants={item} className="flex flex-col gap-2 rounded-2xl bg-neutral-50 border border-neutral-100 p-3">
            <div className="grid grid-cols-2 gap-3">
              <Popover open={returnDateOpen} onOpenChange={setReturnDateOpen}>
                <PopoverTrigger asChild>
                  <button className="w-full h-14 rounded-xl bg-white border border-neutral-200 px-3.5 flex items-center gap-2.5 text-[15px] text-neutral-800" data-testid="booking-return-date">
                    <CalendarDays className="w-4 h-4 text-neutral-500 shrink-0" />
                    <span className="truncate">{fmtDate(returnDate)}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-neutral-100 rounded-2xl" align="start">
                  <Calendar mode="single" selected={returnDate} onSelect={(d) => { if (d) { setReturnDate(d); setReturnDateOpen(false); } }} disabled={(d) => d < (date || new Date(new Date().setHours(0, 0, 0, 0)))} />
                </PopoverContent>
              </Popover>
              <Select value={returnTime} onValueChange={setReturnTime}>
                <SelectTrigger className="w-full h-14 rounded-xl bg-white border border-neutral-200 px-3.5" data-testid="booking-return-time">
                  <span className="!flex items-center gap-2.5 flex-1 text-start"><Clock className="w-4 h-4 text-neutral-500 shrink-0" /><SelectValue placeholder={t("b_time_ph")} /></span>
                </SelectTrigger>
                <SelectContent className="bg-white border-neutral-100 rounded-2xl max-h-64">
                  {times.map((tm) => (<SelectItem key={tm} value={tm} disabled={isPastSlot(returnDate, tm)} className="focus:bg-neutral-50">{tm}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-emerald-600 font-medium" data-testid="booking-return-note">{t("b_return_note")}</p>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div variants={item} className="pt-1">
          <Button onClick={submit} data-testid="booking-submit-widget"
            className="w-full h-16 bg-neutral-900 hover:bg-black text-white rounded-2xl font-semibold text-[17px] transition-colors">
            {t("b_submit")}
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
